import { Input, Label, Textarea } from '@startupquickstart/frontend';
import type { MinigameAuthoringPanelProps } from './types';

interface RelatedWordsConfig {
  theme?: string;
  words?: string[];
  decoyWords?: string[];
}

const DEFAULTS: Required<RelatedWordsConfig> = {
  theme: '',
  words: [],
  decoyWords: [],
};

/** Newline-separated string ↔ string[] for textarea input. */
const toLines = (s: string) =>
  s
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const fromLines = (arr: string[]) => arr.join('\n');

export function RelatedWordsPanel({
  value,
  onChange,
  disabled,
}: MinigameAuthoringPanelProps<RelatedWordsConfig>) {
  const v = { ...DEFAULTS, ...(value ?? {}) };
  const set = (patch: Partial<RelatedWordsConfig>) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">Related words</h4>
        <p className="text-xs text-muted-foreground">
          Players see a mixed grid; they pick the words that fit the theme.
          Decoys are wrong answers shown alongside the real ones.
        </p>
      </div>

      <div className="space-y-1">
        <Label>Theme</Label>
        <Input
          value={v.theme}
          placeholder="Italian food"
          disabled={disabled}
          onChange={(e) => set({ theme: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Correct words (one per line)</Label>
          <Textarea
            value={fromLines(v.words)}
            placeholder={`pasta\npizza\ngelato`}
            rows={6}
            disabled={disabled}
            onChange={(e) => set({ words: toLines(e.target.value) })}
          />
        </div>
        <div className="space-y-1">
          <Label>Decoy words (one per line)</Label>
          <Textarea
            value={fromLines(v.decoyWords)}
            placeholder={`sushi\ncurry\nbaguette`}
            rows={6}
            disabled={disabled}
            onChange={(e) => set({ decoyWords: toLines(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}
